
import { memo, useMemo, useCallback } from 'react';
import { Star, Users } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAmplitudeEvents } from '@/utils/amplitudeEvents';
import { getPropertyShortName } from '@/utils/propertyUtils';
import { titleToSlug } from '@/utils/shared/stringUtils';
import OptimizedCloudinaryImage from '@/components/ui/OptimizedCloudinaryImage';

interface PropertyCardProps {
  id: string;
  title: string;
  shortDescription: string;
  price: number;
  rating: number;
  guests: number;
  images: string[];
  neighborhood?: {
    city: string;
    state: string;
    country: string;
    description: string;
    longDescription: string;
  };
}

const PropertyCard = memo(({ id, title, shortDescription, price, rating, guests, images, neighborhood }: PropertyCardProps) => {
  const [searchParams] = useSearchParams();
  const { trackPropertyClicked } = useAmplitudeEvents();
  
  // Memoize expensive calculations
  const { titleSlug, propertyUrl, propertyShortName, locationDisplay } = useMemo(() => {
    const slug = titleToSlug(title);
    const checkin = searchParams.get('checkin');
    const checkout = searchParams.get('checkout');
    const guestsParam = searchParams.get('guests');
    
    // Build query string with search parameters
    const queryParams = new URLSearchParams();
    if (checkin) queryParams.set('checkin', checkin);
    if (checkout) queryParams.set('checkout', checkout);
    if (guestsParam) queryParams.set('guests', guestsParam);
    
    const queryString = queryParams.toString();
    const url = `/propiedades/${slug}${queryString ? `?${queryString}` : ''}`;
    const shortName = getPropertyShortName(title);
    const location = neighborhood ? `${neighborhood.city}, ${neighborhood.state}` : '';
    
    return {
      titleSlug: slug,
      propertyUrl: url,
      propertyShortName: shortName,
      locationDisplay: location
    };
  }, [title, searchParams, neighborhood]);

  const handlePropertyClick = useCallback(() => {
    trackPropertyClicked(title, propertyShortName, rating, guests, price);
  }, [title, propertyShortName, rating, guests, price, trackPropertyClicked]);
  
  return (
    <Link to={propertyUrl} className="group" onClick={handlePropertyClick}>
      <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 group-hover:scale-[1.02]">
        <div className="relative h-64 overflow-hidden rounded-t-2xl">
          <OptimizedCloudinaryImage
            src={images[0]}
            alt={title}
            className="w-full h-full object-cover will-change-transform group-hover:scale-110 transition-transform duration-500"
            width={600}
            height={400}
            quality={95}
            usePropertyCardOptimization={true}
          />
          <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm rounded-full px-3 py-1">
            <span className="text-sm font-semibold">${price} MXN /noche</span>
          </div>
        </div>
        
        <div className="p-6">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-1">
              <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
              <span className="text-sm font-medium">{rating}</span>
            </div>
            <div className="flex items-center space-x-1 text-gray-500">
              <Users className="w-4 h-4" />
              <span className="text-sm">{guests} huéspedes</span>
            </div>
          </div>
          
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
          <p className="text-gray-600 text-sm mb-2">{shortDescription}</p>
          <p className="text-gray-500 text-sm">{locationDisplay}</p>
        </div>
      </div>
    </Link>
  );
});

PropertyCard.displayName = 'PropertyCard';

export default PropertyCard;
